import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productsAPI, categoriesAPI } from '../services/api';
import { useStore } from '../store/store';
import { 
  ShoppingCartIcon, 
  StarIcon,
  EyeIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const Home: React.FC = () => {
  const { auth, addToCart } = useStore();
  const [quickViewProduct, setQuickViewProduct] = useState<any>(null);

  // Fetch all products
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['home-products'],
    queryFn: async () => {
      const response = await productsAPI.getProducts({ limit: 20, page: 1 });
      return response.data;
    },
  });

  // Fetch top rated products
  const { data: topRatedData, isLoading: topRatedLoading } = useQuery({
    queryKey: ['top-rated-products'],
    queryFn: async () => {
      const response = await productsAPI.getProducts({ 
        limit: 12, 
        page: 1,
        sort: 'ratings',
        order: 'desc'
      });
      return response.data;
    },
  });

  // Fetch categories for brand navigation
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await categoriesAPI.getCategories();
      return response.data;
    },
  });

  const products = productsData?.data || [];
  const topRatedProducts = topRatedData?.data || [];
  const categories = categoriesData?.data || [];

  // Popular brands (similar to touchlcdbaba.com)
  const popularBrands = [
    'Honor', 'Xiaomi Redmi', 'Realme', 'Lenovo', 'Oppo', 
    'Vivo', 'Motorola', 'Nokia', 'Asus', 'One Plus', 'Sony', 'Apple'
  ];

  // Get unique brands from products
  const availableBrands: string[] = Array.from(
    new Set(products.map((p: any) => p.brand).filter((brand: any): brand is string => Boolean(brand) && typeof brand === 'string'))
  );

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleQuickView = (product: any) => {
    setQuickViewProduct(product);
  };

  const handleAddToCart = (product: any) => {
    const cartItem = {
      product: {
        _id: product._id,
        name: product.name,
        brand: product.brand,
        images: product.images,
        pricing: product.pricing
      },
      quantity: 1,
      price: product.currentPricing?.price || product.pricing?.retail?.price || product.retailPrice,
      originalPrice: product.currentPricing?.originalPrice || product.pricing?.retail?.compareAtPrice,
      tier: product.currentPricing?.tier || 'retail',
      discount: product.currentPricing?.discount || 0
    };
    addToCart(cartItem);
  };

  const isNewProduct = (product: any) => {
    const createdAt = new Date(product.createdAt || product.created_at);
    const daysSinceCreation = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceCreation < 30; // Consider products less than 30 days old as "new"
  };

  const isOutOfStock = (product: any) => {
    return (product.inventory?.quantity || 0) === 0;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple Header Banner */}
      <section className="bg-gradient-to-r from-yellow-400 to-yellow-500 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              SS Electronics
            </h1>
            <p className="text-gray-800 text-sm md:text-base">
              Premium Tech Accessories | Special Pricing for Retailers and Distributors
            </p>
          </div>
        </div>
      </section>

      {/* Brand Navigation */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-700 mr-2">Brands:</span>
            {availableBrands.length > 0 ? (
              availableBrands.slice(0, 12).map((brand: string) => (
                <Link
                  key={brand}
                  to={`/products?brand=${encodeURIComponent(brand)}`}
                  className="text-sm text-gray-600 hover:text-yellow-600 px-3 py-1 rounded-md hover:bg-yellow-50 transition-colors"
                >
                  {brand}
                </Link>
              ))
            ) : (
              popularBrands.map((brand) => (
                <Link
                  key={brand}
                  to={`/products?brand=${encodeURIComponent(brand)}`}
                  className="text-sm text-gray-600 hover:text-yellow-600 px-3 py-1 rounded-md hover:bg-yellow-50 transition-colors"
                >
                  {brand}
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Products Grid Section */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Products</h2>
          </div>

          {productsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((product: any) => (
                <div 
                  key={product._id || product.id} 
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow relative group"
                >
                  {/* Product Image */}
                  <div className="relative overflow-hidden bg-gray-100">
                    <Link to={`/products/${product._id || product.id}`}>
                      <img
                        src={product.images?.[0]?.url || 'https://via.placeholder.com/400x400?text=No+Image'}
                        alt={product.name}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </Link>
                    
                    {/* Badges */}
                    <div className="absolute top-2 left-2 flex flex-col gap-2">
                      {isNewProduct(product) && (
                        <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                          New
                        </span>
                      )}
                      {product.isFeatured && (
                        <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded">
                          Featured
                        </span>
                      )}
                    </div>

                    {isOutOfStock(product) && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <span className="bg-red-600 text-white text-sm font-bold px-4 py-2 rounded">
                          Out of stock
                        </span>
                      </div>
                    )}

                    {/* Quick View Button */}
                    <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleQuickView(product)}
                        className="bg-white text-gray-800 hover:bg-yellow-500 hover:text-white p-2 rounded-full shadow-lg transition-colors"
                        title="Quick View"
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-4">
                    <div className="mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase">
                        {product.brand || 'Brand'}
                      </span>
                    </div>
                    
                    <Link to={`/products/${product._id || product.id}`}>
                      <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2 hover:text-yellow-600 transition-colors min-h-[2.5rem]">
                        {product.name}
                      </h3>
                    </Link>

                    {/* Rating */}
                    {product.ratings?.average && (
                      <div className="flex items-center gap-1 mb-2">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <StarIconSolid
                              key={i}
                              className={`w-4 h-4 ${
                                i < Math.floor(product.ratings.average)
                                  ? 'text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-gray-600 ml-1">
                          ({product.ratings.count || 0})
                        </span>
                      </div>
                    )}

                    {/* Price */}
                    <div className="mt-3">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-bold text-gray-900">
                          {formatPrice(product.currentPricing?.price || product.pricing?.retail?.price || product.retailPrice || 0)}
                        </span>
                        {product.currentPricing?.originalPrice && 
                         product.currentPricing.originalPrice > product.currentPricing.price && (
                          <span className="text-sm text-gray-500 line-through">
                            {formatPrice(product.currentPricing.originalPrice)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quick View Button - Mobile */}
                    <div className="mt-4">
                      <Link
                        to={`/products/${product._id || product.id}`}
                        className="block w-full text-center bg-gray-100 hover:bg-yellow-500 hover:text-white text-gray-800 font-medium py-2 px-4 rounded transition-colors"
                      >
                        Quick View
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg">
              <p className="text-gray-500 text-lg">No products available at the moment.</p>
            </div>
          )}

          {/* View All Products Link */}
          {products.length > 0 && (
            <div className="text-center mt-8">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 bg-black hover:bg-gray-900 text-yellow-400 font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                View All Products
                <ShoppingCartIcon className="w-5 h-5" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Top Rated Products Section */}
      {topRatedProducts.length > 0 && (
        <section className="py-8 bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Top Rated Products</h2>
            </div>

            {topRatedLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
                    <div className="h-48 bg-gray-200"></div>
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {topRatedProducts.slice(0, 8).map((product: any) => (
                  <div 
                    key={product._id || product.id} 
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow relative group border-2 border-yellow-200"
                  >
                    {/* Product Image */}
                    <div className="relative overflow-hidden bg-gray-100">
                      <Link to={`/products/${product._id || product.id}`}>
                        <img
                          src={product.images?.[0]?.url || 'https://via.placeholder.com/400x400?text=No+Image'}
                          alt={product.name}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </Link>
                      
                      {/* Rating Badge */}
                      <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded flex items-center gap-1">
                        <StarIconSolid className="w-3 h-3" />
                        {product.ratings?.average?.toFixed(1) || '5.0'}
                      </div>
                    </div>

                    {/* Product Info */}
                    <div className="p-4">
                      <div className="mb-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase">
                          {product.brand || 'Brand'}
                        </span>
                      </div>
                      
                      <Link to={`/products/${product._id || product.id}`}>
                        <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2 hover:text-yellow-600 transition-colors min-h-[2.5rem]">
                          {product.name}
                        </h3>
                      </Link>

                      {/* Price */}
                      <div className="mt-3">
                        <span className="text-xl font-bold text-gray-900">
                          {formatPrice(product.currentPricing?.price || product.pricing?.retail?.price || product.retailPrice || 0)}
                        </span>
                      </div>

                      {/* Quick View Button */}
                      <div className="mt-4">
                        <Link
                          to={`/products/${product._id || product.id}`}
                          className="block w-full text-center bg-gray-100 hover:bg-yellow-500 hover:text-white text-gray-800 font-medium py-2 px-4 rounded transition-colors"
                        >
                          Quick View
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Quick View Modal */}
      {quickViewProduct && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setQuickViewProduct(null)}
        >
          <div 
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Quick View</h2>
                <button
                  onClick={() => setQuickViewProduct(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <img
                    src={quickViewProduct.images?.[0]?.url || 'https://via.placeholder.com/400x400?text=No+Image'}
                    alt={quickViewProduct.name}
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
                <div>
                  <div className="mb-2">
                    <span className="text-sm font-semibold text-gray-500 uppercase">
                      {quickViewProduct.brand}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {quickViewProduct.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {quickViewProduct.shortDescription || quickViewProduct.description}
                  </p>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900">
                      {formatPrice(quickViewProduct.currentPricing?.price || quickViewProduct.pricing?.retail?.price || quickViewProduct.retailPrice || 0)}
                    </span>
                  </div>
                  <div className="flex gap-3">
                    <Link
                      to={`/products/${quickViewProduct._id || quickViewProduct.id}`}
                      className="flex-1 text-center bg-black hover:bg-gray-900 text-yellow-400 font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      View Details
                    </Link>
                    {!isOutOfStock(quickViewProduct) && (
                      <button
                        onClick={() => {
                          handleAddToCart(quickViewProduct);
                          setQuickViewProduct(null);
                        }}
                        className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                      >
                        Add to Cart
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;